/**
 * 最小ルーター(History API)
 *
 * フレームワークを使わない(CLAUDE.md §5)ため手書き。
 * 全ルートは Service Worker が同一のアプリシェルを返すので、
 * QR から /cp?i=..&t=.. で直接起動してもオフラインで動く(§6)。
 */

export type Cleanup = () => void;
export type ScreenFn = (
  root: HTMLElement,
  url: URL,
) => void | Cleanup | Promise<void | Cleanup>;

export interface Route {
  path: string;
  load: () => Promise<ScreenFn>;
  /** ヘッダに出すタイトル。null ならヘッダ非表示(全画面) */
  title: string | null;
}

const routes: Route[] = [
  { path: "/", title: null, load: () => import("./screens/top").then((m) => m.top) },
  { path: "/book", title: "スタンプ帳", load: () => import("./screens/book").then((m) => m.book) },
  { path: "/scan", title: "読み取り", load: () => import("./screens/scan").then((m) => m.scan) },
  { path: "/cp", title: "チェックポイント", load: () => import("./screens/cp").then((m) => m.cp) },
  {
    path: "/achieve",
    title: "達成",
    load: () => import("./screens/achieve").then((m) => m.achieve),
  },
  { path: "/help", title: "ヘルプ", load: () => import("./screens/help").then((m) => m.help) },
  { path: "/staff", title: "受付検証", load: () => import("./screens/staff").then((m) => m.staff) },
  {
    path: "/dashboard",
    title: "集計",
    load: () => import("./screens/dashboard").then((m) => m.dashboard),
  },
];

let root: HTMLElement;
let cleanup: Cleanup | null = null;
let currentPath = "";

const titleListeners = new Set<(title: string | null) => void>();

export function onTitleChange(fn: (title: string | null) => void): () => void {
  titleListeners.add(fn);
  return () => titleListeners.delete(fn);
}

function matchRoute(pathname: string): Route | null {
  // 末尾スラッシュを揺れとして吸収する(印刷物・短縮URLの表記ゆれ対策)
  const p = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  return routes.find((r) => r.path === p) ?? null;
}

export function navigate(to: string, replace = false): void {
  const url = new URL(to, location.href);
  if (url.pathname + url.search === location.pathname + location.search) return;
  if (replace) history.replaceState(null, "", url);
  else history.pushState(null, "", url);
  void render();
}

export async function render(): Promise<void> {
  const url = new URL(location.href);
  const route = matchRoute(url.pathname);

  cleanup?.();
  cleanup = null;
  root.replaceChildren();

  if (!route) {
    for (const fn of titleListeners) fn("ページがありません");
    root.innerHTML = `<div class="pad">
      <p>お探しの画面は見つかりませんでした。</p>
      <p><a class="btn" href="/">最初の画面へ</a></p>
    </div>`;
    return;
  }

  // currentPath を先に更新する。ヘッダ・タブの描画がこれを参照するため、
  // 順序を逆にすると1画面ぶん古いタブがハイライトされる。
  currentPath = route.path;
  for (const fn of titleListeners) fn(route.title);

  try {
    const screen = await route.load();
    // 読み込み中に別画面へ遷移していたら破棄する
    if (currentPath !== route.path) return;
    const result = await screen(root, url);
    cleanup = typeof result === "function" ? result : null;
  } catch (err) {
    console.error("[router]", err);
    root.innerHTML = `<div class="pad">
      <p>画面を表示できませんでした。</p>
      <p class="muted">${String(err)}</p>
      <p><a class="btn" href="/">最初の画面へ</a></p>
    </div>`;
  }
}

export function startRouter(container: HTMLElement): void {
  root = container;

  // アプリ内リンクを横取りしてページ遷移させない(オフラインでの再読込を避ける)
  document.addEventListener("click", (e) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey) return;
    const a = (e.target as HTMLElement | null)?.closest("a");
    if (!a) return;
    const href = a.getAttribute("href");
    if (!href || a.target === "_blank" || a.hasAttribute("download")) return;
    const url = new URL(href, location.href);
    if (url.origin !== location.origin) return;
    e.preventDefault();
    navigate(url.pathname + url.search);
  });

  addEventListener("popstate", () => void render());
  void render();
}

export function currentRoute(): string {
  return currentPath;
}
